/**
 * Base di conoscenza — dettaglio (ADR-0035 RAG). Two tabs over one KB:
 *
 * - **Documenti** — a real TABLE (no accordion): upload (with a per-file progress bar,
 *   since ingest is synchronous, one request per file so the operator sees it advance),
 *   per-document status, delete.
 * - **Ricerca di prova** — the internal vector search engine: type a query, see the
 *   retrieved passages (source · fragment · similarity score) so retrieval quality can be
 *   judged BEFORE publishing (the probe ignores the `published` gate, RBAC-gated on read).
 */
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileText, Loader2, Search, Trash2, Upload } from "lucide-react";

import { usePermissions } from "@/lib/permissions";
import { ApiError } from "@/lib/api";
import {
  type KbSearchResult,
  type KnowledgeBaseDetail,
  knowledgeBasesApi,
} from "@/lib/api-knowledge-bases";

/** Map an upload HTTP error to a friendly, per-file IT message. The backend's
 *  `file_too_large` code carries the configured limit MB (`…:50`) so we name it. */
function uploadErrorMessage(name: string, err: unknown): string {
  const code = err instanceof ApiError ? err.message : "";
  const tooLarge = code.match(/file_too_large(?::(\d+))?/);
  if (tooLarge)
    return `${name}: supera il limite${tooLarge[1] ? ` di ${tooLarge[1]} MB` : " configurato"}.`;
  if (code.includes("too_many_files")) return `${name}: troppi file in una volta.`;
  if (err instanceof ApiError && (err.status === 401 || err.status === 403))
    return `${name}: permesso negato.`;
  if (err instanceof ApiError && err.status >= 500)
    return `${name}: errore del server durante l'elaborazione.`;
  return `${name}: caricamento non riuscito.`;
}

/** Map a `failed` document's stable error code to a friendly IT reason. */
function docErrorMessage(code: string | null): string | null {
  if (!code) return null;
  if (code.includes("sandbox_required"))
    return "Sandbox non configurata (serve per estrarre PDF/DOCX).";
  if (code.includes("empty_document"))
    return "Nessun testo estraibile (forse un PDF scansionato/immagine).";
  if (code.includes("extract_failed")) return "Estrazione del testo non riuscita.";
  if (code.includes("dimension_mismatch")) return "Dimensione del vettore incompatibile.";
  if (code.includes("embedding")) return "Embedding non riuscito.";
  return code;
}

const btnCls =
  "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-muted disabled:opacity-50";
const inputCls = "rounded-md border border-border bg-background px-2.5 py-1.5 text-sm";

const STATUS_LABEL: Record<string, string> = {
  pending: "In attesa",
  processing: "In corso",
  ready: "Pronto",
  failed: "Errore",
};

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function KnowledgeBaseDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can("knowledge_bases", "write");
  const qc = useQueryClient();
  const detailKey = ["knowledge-bases", id, "detail"] as const;
  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () => knowledgeBasesApi.get(id),
    enabled: !!id,
  });
  const [tab, setTab] = useState<"docs" | "search">("docs");

  const setPublished = useMutation({
    mutationFn: (v: boolean) => knowledgeBasesApi.update(id, { published: v }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: detailKey });
      void qc.invalidateQueries({ queryKey: ["knowledge-bases"] });
    },
  });

  const d = detail.data;

  return (
    <div className="mx-auto max-w-4xl p-6">
      <button
        type="button"
        className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        onClick={() => navigate("/knowledge-bases")}
      >
        <ArrowLeft className="h-4 w-4" /> Basi di conoscenza
      </button>

      {detail.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {detail.isError && (
        <p className="text-sm text-danger">Base di conoscenza non trovata.</p>
      )}

      {d && (
        <>
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 text-lg font-semibold">
                {d.name}
                {d.published ? (
                  <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[11px] text-emerald-600 dark:text-emerald-400">
                    Pubblicata
                  </span>
                ) : (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                    Bozza
                  </span>
                )}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {d.document_count} documenti · {d.chunk_count} frammenti
                {d.dimension ? ` · vettori a ${d.dimension} dimensioni` : " · nessun vettore ancora"}
                {d.description ? ` · ${d.description}` : ""}
              </p>
            </div>
            {canWrite && (
              <button
                type="button"
                className={btnCls}
                disabled={setPublished.isPending}
                onClick={() => setPublished.mutate(!d.published)}
              >
                {d.published ? "Ritira" : "Pubblica"}
              </button>
            )}
          </div>

          <div className="mb-4 flex gap-1 border-b border-border">
            <TabButton active={tab === "docs"} onClick={() => setTab("docs")}>
              Documenti
            </TabButton>
            <TabButton active={tab === "search"} onClick={() => setTab("search")}>
              Ricerca di prova
            </TabButton>
          </div>

          {tab === "docs" ? (
            <DocumentsTab kb={d} canWrite={canWrite} detailKey={detailKey} />
          ) : (
            <SearchTab kbId={id} ready={d.dimension != null} />
          )}
        </>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={
        "border-b-2 px-3 py-1.5 text-sm -mb-px " +
        (active
          ? "border-foreground font-medium text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground")
      }
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// --- Documenti -------------------------------------------------------------

function DocumentsTab({
  kb,
  canWrite,
  detailKey,
}: {
  kb: KnowledgeBaseDetail;
  canWrite: boolean;
  detailKey: readonly unknown[];
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<{ done: number; total: number; current: string } | null>(
    null,
  );
  const [errors, setErrors] = useState<string[]>([]);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: detailKey });
    await qc.invalidateQueries({ queryKey: ["knowledge-bases"] });
  };

  // Sequential, one request per file — ingest is synchronous, so this lets the bar
  // advance per file and the newly-ingested document appears as soon as it's done.
  async function uploadFiles(files: File[]) {
    setErrors([]);
    const errs: string[] = [];
    for (let i = 0; i < files.length; i++) {
      setProgress({ done: i, total: files.length, current: files[i].name });
      try {
        await knowledgeBasesApi.uploadDocuments(kb.id, [files[i]]);
      } catch (e) {
        errs.push(uploadErrorMessage(files[i].name, e));
      }
      await refresh();
    }
    setProgress(null);
    setErrors(errs);
  }

  const delDoc = useMutation({
    mutationFn: (docId: string) => knowledgeBasesApi.deleteDocument(kb.id, docId),
    onSuccess: refresh,
  });

  const uploading = progress !== null;
  const pct = progress ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Documenti
        </span>
        {canWrite && (
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-muted-foreground sm:inline">
              PDF, MD, DOCX, TXT…
            </span>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = "";
                if (files.length) void uploadFiles(files);
              }}
            />
            <button
              type="button"
              className={btnCls}
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Carica
            </button>
          </div>
        )}
      </div>

      {progress && (
        <div className="mb-3 rounded-md border border-border p-3">
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              <span className="truncate">
                Elaborazione {Math.min(progress.done + 1, progress.total)}/{progress.total} —{" "}
                {progress.current}
              </span>
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">{pct}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}
      {errors.length > 0 && (
        <ul className="mb-2 space-y-0.5">
          {errors.map((msg, i) => (
            <li key={i} className="text-xs text-danger">
              {msg}
            </li>
          ))}
        </ul>
      )}

      {kb.documents.length === 0 && !uploading ? (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nessun documento. Carica file (PDF, Markdown, DOCX, TXT…) per indicizzarli.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-1.5 pr-2 font-medium">Documento</th>
              <th className="px-2 py-1.5 font-medium">Dimensione</th>
              <th className="px-2 py-1.5 font-medium">Stato</th>
              <th className="px-2 py-1.5 text-right font-medium">Frammenti</th>
              {canWrite && <th className="w-8" />}
            </tr>
          </thead>
          <tbody>
            {kb.documents.map((doc) => (
              <tr key={doc.id} className="border-b border-border/50">
                <td className="py-1.5 pr-2">
                  <span className="flex min-w-0 items-center gap-2">
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate">{doc.filename}</span>
                  </span>
                </td>
                <td className="px-2 text-muted-foreground">{fmtBytes(doc.byte_size)}</td>
                <td className="px-2">
                  <span
                    className={
                      doc.status === "failed"
                        ? "text-danger"
                        : doc.status === "ready"
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-muted-foreground"
                    }
                    title={doc.error ?? undefined}
                  >
                    {STATUS_LABEL[doc.status] ?? doc.status}
                  </span>
                  {doc.status === "failed" && docErrorMessage(doc.error) && (
                    <span className="block text-xs text-muted-foreground">
                      {docErrorMessage(doc.error)}
                    </span>
                  )}
                </td>
                <td className="px-2 text-right tabular-nums text-muted-foreground">
                  {doc.status === "ready" ? doc.chunk_count : "—"}
                </td>
                {canWrite && (
                  <td className="text-right">
                    <button
                      type="button"
                      aria-label="Elimina documento"
                      className="text-muted-foreground hover:text-danger"
                      onClick={() => delDoc.mutate(doc.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Ricerca di prova ------------------------------------------------------

const NOTE_MSG: Record<string, string> = {
  not_ready: "La base non ha ancora documenti indicizzati. Carica dei file e attendi l'elaborazione.",
  no_matches: "Nessun passaggio rilevante per questa query.",
  empty_query: "Inserisci una query.",
  embedding_config_not_found: "Config di embedding non trovata.",
};

function noteMessage(note: string): string {
  if (note.startsWith("embed_failed")) {
    const detail = note.slice("embed_failed".length).replace(/^:/, "");
    return `Embedding non riuscito${detail ? ` (${detail})` : ""}.`;
  }
  return NOTE_MSG[note] ?? note;
}

function SearchTab({ kbId, ready }: { kbId: string; ready: boolean }) {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<KbSearchResult | null>(null);
  const run = useMutation({
    mutationFn: () => knowledgeBasesApi.search(kbId, query.trim(), 8),
    onSuccess: (r) => setResult(r),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) run.mutate();
  };

  return (
    <div>
      {!ready && (
        <p className="mb-3 rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
          Questa base non ha ancora vettori. Carica dei documenti nella scheda «Documenti»;
          potrai poi testare il recupero qui.
        </p>
      )}
      <form onSubmit={submit} className="mb-4 flex gap-2">
        <input
          className={inputCls + " flex-1"}
          placeholder="Fai una domanda come la farebbe l'agente…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button
          type="submit"
          className={btnCls}
          disabled={!query.trim() || run.isPending}
        >
          {run.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
          Cerca
        </button>
      </form>

      {run.isError && (
        <p className="text-sm text-danger">Ricerca non riuscita. Riprova.</p>
      )}

      {result && result.passages.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {noteMessage(result.note ?? "no_matches")}
        </p>
      )}

      {result && result.passages.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {result.passages.length} passaggi recuperati, ordinati per pertinenza.
          </p>
          {result.passages.map((p, i) => (
            <div key={i} className="rounded-md border border-border p-3">
              <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="flex min-w-0 items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">
                    {p.source} · frammento #{p.ordinal}
                  </span>
                </span>
                <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 tabular-nums">
                  {(p.score * 100).toFixed(0)}%
                </span>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm">{p.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
